import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Select from "react-select";
import Spinner from "react-bootstrap/Spinner";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosClient from "../../../config/axios.js";
import { useAuth } from "../../../context/AuthContext";
import {
  getOrdersBillingColumns,
  customStyles,
} from "./ordersBillingTableConfig";

const validationSchema = Yup.object().shape({
  startDate: Yup.date()
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .max(
      Yup.ref("endDate"),
      "La fecha de inicio no puede ser mayor que la fecha de fin"
    ),
  endDate: Yup.date()
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .min(
      Yup.ref("startDate"),
      "La fecha de fin no puede ser menor que la fecha de inicio"
    ),
});

function OrdersBillingTable({ orderStatusCode, updateFlag }) {
  const [data, setData] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const { userId} = useAuth();
  const [clientTypes, setClientTypes] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  const { isAdmin } = useAuth()
  
  const fetchData = async (filters) => {
    try {
      const params = {
        order_status_code: orderStatusCode,
        start: (currentPage - 1) * rowsPerPage,
        length: rowsPerPage,
        search: filters.searchValue || "",
        baseUserId: filters.baseUser?.value,
        insuranceId: filters.insurance?.value,
        operatorId: filters.operator?.value,
        clientTypeId: filters.clientType?.value,
        orderStatusId: filters.orderStatus?.value,
        sort: sortField,
        dir: sortDirection,
      };

      if (filters.startDate && filters.endDate) {
        params.startDate = filters.startDate;
        params.endDate = filters.endDate;
      }

      const response = await axiosClient.get(
        `/report-invoice-cartaporte-orders/datatable`,
        { params }
      );

      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      startDate: "",
      endDate: "",
      searchValue: "",
      baseUser: "",
      operator: "",
      insurance: "",
      clientType: "",
      orderStatus: "",
    },
    validationSchema,
    onSubmit: (values) => {
      setLoading(true);
      setCurrentPage(1);
      fetchData(values);
    },
  });

  useEffect(() => {
    axiosClient.get("/users/allUsers").then((response) => {
      const formattedOptions = response.data.data.map((user) => ({
        value: user.id,
        label: (
          user.name +
          " " +
          user.first_surname +
          " " +
          (user.second_surname ? user.second_surname : "")
        ).trim(),
      }));
      const userIdInt = parseInt(userId ?? "-1");
      const currentUser = formattedOptions.find(
        (user) => user.value === userIdInt
      );
      setUsuarios(formattedOptions);
      formik.setFieldValue("baseUser", currentUser);
    });

    axiosClient.get("/users/allOperators").then((response) => {
      const formattedOptions = response.data.data.map((user) => ({
        value: user.id,
        label: (
          user.name +
          " " +
          user.first_surname +
          " " +
          (user.second_surname ? user.second_surname : "")
        ).trim(),
      }));
      setOperadores(formattedOptions);
    });

    axiosClient.get("/insurers/all").then((response) => {
      const formattedOptions = response.data.data.map((insurance) => ({
        value: insurance.id,
        label: insurance.insurance_name,
      }));
      setInsurers(formattedOptions);
    });

    axiosClient.get("/clientTypes/all").then((response) => {
      const formattedOptions = response.data.data.map((ct) => ({
        value: ct.id,
        label: ct.client_type,
      }));
      setClientTypes(formattedOptions);
    });

    axiosClient.get("/orderStatuses/allWithOrderCount").then((response) => {
      const formattedOptions = response.data.data.map((status) => ({
        value: status.id,
        label: `${status.order_status} (${status.count_orders})`,
      }));
      setOrderStatuses(formattedOptions);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchData(formik.values);
  }, [
    currentPage,
    rowsPerPage,
    sortField,
    sortDirection,
    orderStatusCode,
    updateFlag,
  ]);

  const clearFilters = () => {
    formik.resetForm();
    setLoading(true);
    fetchData({
      startDate: "",
      endDate: "",
      searchValue: "",
      baseUser: "",
      operator: "",
      insurance: "",
      clientType: "",
      orderStatus: "",
    });
  };

  return (
    <div className="table-responsive mb-3">
      <DataTable
        columns={getOrdersBillingColumns(orderStatusCode, fetchData, isAdmin)}
        data={data.data}
        progressPending={loading}
        sortServer
        pagination
        paginationServer
        paginationPerPage={rowsPerPage}
        paginationTotalRows={data.recordsFiltered}
        paginationDefaultPage={currentPage}
        subHeader
        subHeaderAlign="center"
        customStyles={customStyles}
        pointerOnHover
        paginationRowsPerPageOptions={[50, 100, 200, 500]}
        paginationComponentOptions={{
          rowsPerPageText: "Filas por página",
          rangeSeparatorText: "de",
        }}
        noDataComponent="Sin información por mostrar"
        progressComponent={
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        }
        subHeaderComponent={
          <form
            className="row vw-100 align-items-end"
            onSubmit={formik.handleSubmit}
          >
            <div className="col-sm-3 col-md-3 mb-3">
              <label htmlFor="startDate" className="form-label">
                Fecha inicio
              </label>
              <input
                type="date"
                name="startDate"
                className="form-control"
                value={formik.values.startDate}
                onChange={formik.handleChange}
              />
              {formik.errors.startDate && (
                <div className="text-danger small">
                  {formik.errors.startDate}
                </div>
              )}
            </div>

            <div className="col-sm-3 col-md-3 mb-3">
              <label htmlFor="startDate" className="form-label">
                Fecha fin
              </label>
              <input
                type="date"
                name="endDate"
                className="form-control"
                value={formik.values.endDate}
                onChange={formik.handleChange}
              />
              {formik.errors.endDate && (
                <div className="text-danger small">{formik.errors.endDate}</div>
              )}
            </div>
            {/* <div className="col-sm-3 col-md-4 mb-3">
              <Select
                name="baseUser"
                options={usuarios}
                value={formik.values.baseUser}
                placeholder="Filtrar por usuario que registra la Orden"
                isClearable
                isSearchable
                onChange={(value) => formik.setFieldValue("baseUser", value)}
              />
            </div>

            <div className="col-sm-4 col-md-4 mb-3">
              <Select
                name="operator"
                options={operadores}
                value={formik.values.operator}
                placeholder="Filtrar por Operador"
                isClearable
                isSearchable
                onChange={(value) => formik.setFieldValue("operator", value)}
              />
            </div> */}
            <div className="col-sm-3 col-md-3 mb-3">
              <Select
                name="clientType"
                options={clientTypes}
                value={formik.values.clientType}
                placeholder="Filtrar por tipo de cliente"
                isClearable
                isSearchable
                onChange={(value) => formik.setFieldValue("clientType", value)}
              />
            </div>

            <div className="col-sm-3 col-md-3 mb-3">
              <Select
                name="insurance"
                options={insurers}
                value={formik.values.insurance}
                placeholder="Filtrar por aseguradora"
                isClearable
                isSearchable
                onChange={(value) => formik.setFieldValue("insurance", value)}
              />
            </div>
            <div className="col-sm-3 col-md-3 mb-3">
              <Select
                name="orderStatus"
                options={orderStatuses}
                value={formik.values.orderStatus}
                placeholder="Filtrar por estado de Orden"
                isClearable
                isSearchable
                onChange={(value) => formik.setFieldValue("orderStatus", value)}
              />
            </div>

            <div className="col-sm-3 col-md-3 ml-auto mb-3 d-flex gap-2">
              <button
                type="button"
                className="btn btn-secondary w-100"
                onClick={clearFilters}
              >
                Limpiar filtros
              </button>
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={formik.handleSubmit}
              >
                Buscar
              </button>
            </div>
            <div className="col-auto ms-auto mb-3">
              <div className="input-group">
                <input
                  type="text"
                  name="searchValue"
                  className="form-control form-control-md"
                  placeholder="Buscar..."
                  value={formik.values.searchValue}
                  onChange={formik.handleChange}
                  onKeyDown={(e) => e.key === "Enter" && formik.handleSubmit()}
                />
                <div
                  className="input-group-text btn btn-info"
                  onClick={formik.handleSubmit}
                >
                  <i className="fa fa-search"></i>
                </div>
              </div>
            </div>
          </form>
        }
        onChangePage={(page) => setCurrentPage(page)}
        onChangeRowsPerPage={(newPerPage) => {
          setRowsPerPage(newPerPage);
          setCurrentPage(1);
        }}
        onSort={(column, sortDirection) => {
          setSortDirection(sortDirection);
          setSortField(column.sortField);
          setLoading(true);
          setCurrentPage(1);
        }}
      />
    </div>
  );
}

export default OrdersBillingTable;
